import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { callAdminAPI } from "../../api/callAdmin.js";

const PAGE_LIMIT = 100;

function todayISO() {
    return new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Kolkata",
    }).format(new Date());
}

function ProgressBar({ called, assigned }) {
    const pct = assigned > 0 ? Math.round((called / assigned) * 100) : 0;
    const color = pct >= 80 ? "#16a34a" : pct >= 50 ? "#f59e0b" : "#ef4444";
    return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
                style={{
                    flex: 1,
                    height: "6px",
                    borderRadius: "4px",
                    background: "#e5e7eb",
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        width: `${pct}%`,
                        height: "100%",
                        borderRadius: "4px",
                        background: color,
                        transition: "width 0.3s",
                    }}
                />
            </div>
            <span
                style={{
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    color,
                    minWidth: "32px",
                    textAlign: "right",
                }}
            >
                {pct}%
            </span>
        </div>
    );
}

export default function DailyCallTrackerPage() {
    const navigate = useNavigate();

    const [date, setDate] = useState(todayISO());
    const [search, setSearch] = useState("");
    const [offset, setOffset] = useState(0);

    const [summary, setSummary] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const debounceRef = useRef(null);

    const isToday = date === todayISO();
    const totalPages = Math.ceil(total / PAGE_LIMIT);
    const currentPage = Math.floor(offset / PAGE_LIMIT) + 1;

    const load = useCallback(async ({ d, q, off, silent = false } = {}) => {
        if (!silent) setLoading(true);
        try {
            const res = await callAdminAPI.getDailyTracker({
                date: d,
                search: q,
                limit: PAGE_LIMIT,
                offset: off,
            });
            setSummary(res.data?.data || []);
            setTotal(res.data?.total ?? 0);
        } catch {
            setSummary([]);
            setTotal(0);
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    // Reload when date changes
    useEffect(() => {
        setOffset(0);
        load({ d: date, q: search, off: 0 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    // Reload when offset changes (page nav)
    useEffect(() => {
        load({ d: date, q: search, off: offset });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offset]);

    // Debounced search (400 ms)
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            const newOffset = 0;
            setOffset(newOffset);
            load({ d: date, q: search, off: newOffset });
        }, 400);
        return () => clearTimeout(debounceRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    // Auto-refresh every 60 s when viewing today
    useEffect(() => {
        if (!isToday) return;
        const id = setInterval(
            () => load({ d: date, q: search, off: offset, silent: true }),
            60_000,
        );
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isToday, date, search, offset]);

    const totals = summary.reduce(
        (acc, r) => ({
            assigned: acc.assigned + r.assigned,
            called: acc.called + r.called,
            missed: acc.missed + r.missed,
        }),
        { assigned: 0, called: 0, missed: 0 },
    );

    const goPage = (p) => {
        const newOffset = (p - 1) * PAGE_LIMIT;
        if (newOffset !== offset) setOffset(newOffset);
    };

    return (
        <div
            className="page-wrapper"
            style={{ background: "#fff", minHeight: "100vh" }}
        >
            <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }`}</style>

            {/* Header */}
            <div
                className="page-header"
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "12px",
                    marginBottom: "12px",
                }}
            >
                <div>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                        }}
                    >
                        <h1 className="page-title">Daily Call Tracker</h1>
                    </div>
                </div>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                    }}
                >
                    {/* User search */}
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            border: "1.5px solid #e5e7eb",
                            background: "#f9fafb",
                        }}
                    >
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#9ca3af"
                            strokeWidth="2.5"
                        >
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Filter by user…"
                            style={{
                                border: "none",
                                background: "transparent",
                                outline: "none",
                                fontSize: "0.88rem",
                                color: "#374151",
                                width: "150px",
                            }}
                        />
                        {search && (
                            <button
                                onClick={() => setSearch("")}
                                style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    color: "#9ca3af",
                                    display: "flex",
                                    padding: 0,
                                    lineHeight: 1,
                                }}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                    {/* Date picker */}
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => {
                            setDate(e.target.value);
                            setSearch("");
                        }}
                        style={{
                            padding: "8px 12px",
                            borderRadius: "8px",
                            border: "1.5px solid #e5e7eb",
                            fontSize: "0.88rem",
                            fontWeight: 600,
                            color: "#374151",
                            outline: "none",
                            cursor: "pointer",
                        }}
                    />
                </div>
            </div>

            {/* Summary cards */}
            {!loading && summary.length > 0 && (
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: "14px",
                        marginBottom: "24px",
                    }}
                >
                    {[
                        {
                            label: "Total Assigned",
                            value: totals.assigned,
                            color: "#6366f1",
                        },
                        {
                            label: "Total Called",
                            value: totals.called,
                            color: "#16a34a",
                        },
                        {
                            label: "Total Pending",
                            value: totals.missed,
                            color: "#f59e0b",
                        },
                    ].map((c) => (
                        <div
                            key={c.label}
                            style={{
                                background: "#f9fafb",
                                border: "1.5px solid #e5e7eb",
                                borderRadius: "10px",
                                padding: "16px 20px",
                            }}
                        >
                            <div
                                style={{
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    color: "#6b7280",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.05em",
                                    marginBottom: "6px",
                                }}
                            >
                                {c.label}
                            </div>
                            <div
                                style={{
                                    fontSize: "1.6rem",
                                    fontWeight: 800,
                                    color: c.color,
                                }}
                            >
                                {c.value}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Table */}
            {loading ? (
                <div
                    style={{
                        padding: "60px",
                        textAlign: "center",
                        color: "#9ca3af",
                        fontSize: "0.9rem",
                    }}
                >
                    Loading…
                </div>
            ) : summary.length === 0 ? (
                <div className="empty-state">
                    <div style={{ fontSize: "2.5rem", marginBottom: "12px" }}>
                        📋
                    </div>
                    <h3>
                        {search
                            ? `No users match "${search}"`
                            : `No report data for ${date}`}
                    </h3>
                    {!search && (
                        <p
                            style={{
                                color: "#9ca3af",
                                marginTop: "6px",
                                fontSize: "0.88rem",
                            }}
                        >
                            Reports are archived daily when the queue cron runs.
                            Try a past date.
                        </p>
                    )}
                </div>
            ) : (
                <>
                    <div className="table-card">
                        <div className="table-wrap">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>CRM User</th>
                                        <th style={{ textAlign: "center" }}>
                                            Assigned
                                        </th>
                                        <th style={{ textAlign: "center" }}>
                                            Called
                                        </th>
                                        <th style={{ textAlign: "center" }}>
                                            Pending
                                        </th>
                                        <th style={{ textAlign: "center" }}>
                                            Skipped
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summary.map((row, i) => (
                                        <tr
                                            key={row.crm_user_id}
                                            onClick={() =>
                                                navigate(
                                                    `/daily-call-tracker/${date}/${row.crm_user_id}`,
                                                    {
                                                        state: {
                                                            userName:
                                                                row.crm_user_name,
                                                        },
                                                    },
                                                )
                                            }
                                            style={{ cursor: "pointer" }}
                                            title="Click to see lead list"
                                        >
                                            <td className="text-muted">
                                                {offset + i + 1}
                                            </td>
                                            <td>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "10px",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: "32px",
                                                            height: "32px",
                                                            borderRadius: "50%",
                                                            background:
                                                                "rgba(99,102,241,0.12)",
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                            fontWeight: 700,
                                                            fontSize: "0.85rem",
                                                            color: "#6366f1",
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {row.crm_user_name
                                                            .charAt(0)
                                                            .toUpperCase()}
                                                    </div>
                                                    <span
                                                        style={{
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {row.crm_user_name}
                                                    </span>
                                                </div>
                                            </td>
                                            <td
                                                style={{
                                                    textAlign: "center",
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {row.assigned}
                                            </td>
                                            <td
                                                style={{
                                                    textAlign: "center",
                                                    fontWeight: 700,
                                                    color: "#16a34a",
                                                }}
                                            >
                                                {row.called}
                                            </td>
                                            <td
                                                style={{
                                                    textAlign: "center",
                                                    fontWeight: 700,
                                                    color: "#f59e0b",
                                                }}
                                            >
                                                {row.missed}
                                            </td>
                                            <td
                                                style={{
                                                    textAlign: "center",
                                                    color: "#9ca3af",
                                                }}
                                            >
                                                {row.skipped || 0}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginTop: "16px",
                                padding: "0 4px",
                            }}
                        >
                            <span
                                style={{
                                    fontSize: "0.83rem",
                                    color: "#6b7280",
                                }}
                            >
                                Showing {offset + 1}–
                                {Math.min(offset + PAGE_LIMIT, total)} of{" "}
                                {total} users
                            </span>
                            <div style={{ display: "flex", gap: "6px" }}>
                                <button
                                    onClick={() => goPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    style={{
                                        padding: "6px 14px",
                                        borderRadius: "7px",
                                        border: "1.5px solid #e5e7eb",
                                        background:
                                            currentPage === 1
                                                ? "#f9fafb"
                                                : "#fff",
                                        color:
                                            currentPage === 1
                                                ? "#d1d5db"
                                                : "#374151",
                                        fontSize: "0.83rem",
                                        fontWeight: 600,
                                        cursor:
                                            currentPage === 1
                                                ? "not-allowed"
                                                : "pointer",
                                    }}
                                >
                                    ← Prev
                                </button>
                                <span
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        padding: "6px 14px",
                                        borderRadius: "7px",
                                        border: "1.5px solid #6366f1",
                                        background: "rgba(99,102,241,0.08)",
                                        color: "#6366f1",
                                        fontSize: "0.83rem",
                                        fontWeight: 700,
                                    }}
                                >
                                    {currentPage} / {totalPages}
                                </span>
                                <button
                                    onClick={() => goPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    style={{
                                        padding: "6px 14px",
                                        borderRadius: "7px",
                                        border: "1.5px solid #e5e7eb",
                                        background:
                                            currentPage === totalPages
                                                ? "#f9fafb"
                                                : "#fff",
                                        color:
                                            currentPage === totalPages
                                                ? "#d1d5db"
                                                : "#374151",
                                        fontSize: "0.83rem",
                                        fontWeight: 600,
                                        cursor:
                                            currentPage === totalPages
                                                ? "not-allowed"
                                                : "pointer",
                                    }}
                                >
                                    Next →
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
